/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, ReactNode} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {SeverityTag} from '../../tags/current/SeverityTag';
import {getAssetDetailsContentStyles} from './assetDetailsContentStyles';

export interface EmptyStateRowProps {
  message: string;
  // the grey availability tag's text, e.g. `No Data` ...
  tagLabel: string;
  // an icon shown before the tag, e.g. the sensor's own icon ...
  icon?: ReactNode;
}

const useStyles = makeStyles(() => {
  const {text} = getAssetDetailsContentStyles();
  return {
    row : {
      display        : 'flex',
      width          : '100%',
      alignItems     : 'center',
      justifyContent : 'space-between',
      gap            : '10px'
    },
    message : text,
    status  : {
      display    : 'flex',
      flexShrink : 0,
      alignItems : 'center',
      gap        : '8px'
    }
  };
});

/**
 * Renders the Asset Details Drawer's current theme empty state row - its
 * message on the left and, on the right, an optional icon and a grey
 * availability tag (`SeverityTag`, `default`) - as on the SC sensor and
 * Spreader cards without data.
 *
 * ```typescript
 *
 *   import {EmptyStateRow} from
 *     '@ion/dls/src/components/assetdetails/current/EmptyStateRow';
 *   ...
 *
 *   <EmptyStateRow message='No Data To Display from the Past 24 Hours'
 *     tagLabel='No Data'
 *     icon={<TemperatureIcon size={IconSize.medium} />} />
 *
 * ```
 *
 * @param   message  The empty state's message.
 * @param   tagLabel The availability tag's text.
 * @param   icon     An icon shown before the tag, if any.
 * @returns          The empty state row.
 */
export const EmptyStateRow = ({message, tagLabel, icon}: EmptyStateRowProps) => {
  const classes = useStyles();

  return (
    <div className={classes.row}>
      <span className={classes.message}>
        {message}
      </span>
      <div className={classes.status}>
        {icon}
        <SeverityTag label={tagLabel} severity='default' />
      </div>
    </div>
  );
};

export default memo(EmptyStateRow);
